import { useReducer, useRef, useEffect } from "react";
import { wizardReducer, initialState } from "./wizardReducer";
import { STEP_ORDER } from "./wizardTypes";
import type { WizardState, WizardAction, BuildPlanResponse } from "./wizardTypes";
import {
  mixpanelPlgTrack,
  mixpanelPlgRegister,
  mixpanelPlgUnregister,
} from "src/services/mixpanelService";

const SESSION_ID_PROP = "signup_session_id";

/** Analytics receive only the email domain, never the full address. */
export function emailDomain(email?: string): string {
  if (!email) return "";
  return email.split("@")[1] ?? "";
}

/** A per-wizard funnel join key, independent of Mixpanel's visitor identity. */
function newSessionId(): string {
  // Unlike randomUUID, getRandomValues also works outside a secure context.
  const c = globalThis.crypto;
  if (c?.randomUUID) return c.randomUUID();
  const bytes = c.getRandomValues(new Uint8Array(16));
  return `sid-${Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("")}`;
}

/** Normalize mixed billing periods to MRR, then derive ARR; matches SignupSlack. */
export function planRevenue(plan: BuildPlanResponse | null): { mrr: number; arr: number } {
  if (!plan) return { mrr: 0, arr: 0 };
  const monthly = plan.lines.reduce(
    (sum, l) => sum + (l.period === "mo" ? l.price : l.price / 12),
    0,
  );
  const mrr = Math.round(monthly * 100) / 100;
  return { mrr, arr: Math.round(mrr * 12 * 100) / 100 };
}

/** Track committed step changes outside the reducer, which React may replay. */
function trackStepEntry(state: WizardState, secondsElapsed: number): void {
  const plan = state.plan;
  const { mrr, arr } = planRevenue(plan);

  switch (state.step) {
    // Funnel top only. Later steps are already marked by signup_started,
    // plan_confirmed and signup_completed; a second event per step would be
    // a competing source of truth.
    case "questionnaire":
      mixpanelPlgTrack("signup_step_viewed", {
        step: state.step,
        step_index: STEP_ORDER.indexOf(state.step),
      });
      break;

    case "plan":
      mixpanelPlgTrack("signup_started", {
        needs: state.answers.needs,
        volumes: state.answers.volumes,
        email_domain: emailDomain(state.answers.email),
        mrr,
        arr,
        line_items: (plan?.lines ?? []).map((l) => ({
          key: l.productKey,
          name: l.name,
          price: l.price,
          period: l.period,
        })),
        product_keys: (plan?.lines ?? []).map((l) => l.productKey),
        over_ceiling: plan?.overCardLimit ?? false,
      });
      if (state.toSales) {
        mixpanelPlgTrack("sales_lead_created", {
          reason: "over_ceiling",
          mrr,
          arr,
          email_domain: emailDomain(state.answers.email),
        });
      }
      break;

    case "payment":
      mixpanelPlgTrack("plan_confirmed", { mrr, arr });
      break;

    case "confirmation":
      mixpanelPlgTrack("signup_completed", {
        acct_id: state.payment?.accountId ?? "",
        mrr,
        arr,
        charged_amount: state.payment?.chargedAmount ?? 0,
        username_set: !!state.account?.username,
        seconds_elapsed: secondsElapsed,
        test_mode: state.account?.isTestSignup === true,
      });
      break;
  }
}

type WizardAnalytics = {
  state: WizardState;
  dispatch: (action: WizardAction) => void;
  /** Call when the payment form is submitted — the one stage the state machine cannot see. */
  trackPaymentStarted: () => void;
  /** Call from the PaymentStep catch block — the other event outside the step seam. */
  trackPaymentFailed: (reasonCode: string) => void;
};

export function useWizardAnalytics(): WizardAnalytics {
  const startTime = useRef(Date.now());
  const [state, dispatch] = useReducer(wizardReducer, initialState);

  // Lazily initialised so the id is generated once, not on every render.
  const sessionId = useRef<string>();
  if (!sessionId.current) sessionId.current = newSessionId();

  // Suppress StrictMode replays, but retain real revisits and edited plans.
  // The funnel counts distinct session IDs per stage.
  const lastReported = useRef<WizardState | null>(null);

  // Register before tracking. The property stays in memory; cleanup also
  // removes it on SPA navigation, where the Mixpanel instance survives.
  useEffect(() => {
    mixpanelPlgRegister({ [SESSION_ID_PROP]: sessionId.current });
    return () => mixpanelPlgUnregister(SESSION_ID_PROP);
  }, []);

  useEffect(() => {
    if (lastReported.current === state) return;
    lastReported.current = state;
    trackStepEntry(state, Math.round((Date.now() - startTime.current) / 1000));
  }, [state]);

  const trackPaymentStarted = () => {
    const { mrr, arr } = planRevenue(state.plan);
    mixpanelPlgTrack("payment_started", { mrr, arr });
  };

  const trackPaymentFailed = (reasonCode: string) => {
    mixpanelPlgTrack("payment_failed", { reason_code: reasonCode });
  };

  return { state, dispatch, trackPaymentStarted, trackPaymentFailed };
}
